import React from 'react';
import { motion } from 'framer-motion';
import { Link as ScrollLink } from 'react-scroll';
import { ArrowRight, ShieldCheck, Activity, Cpu, Lock, Layers } from 'lucide-react';
import Button from '../ui/Button';

const Hero = () => {
  return (
    <section id="home" className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-gradient-to-b from-slate-50/80 via-white to-white dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 overflow-hidden">
      {/* Subtle Architectural Grid Background */}
      <div 
        className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-60 pointer-events-none" 
      />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Core Messaging & CTAs */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7 max-w-2xl"
          >
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-brand-50 dark:bg-brand-950/60 border border-brand-200/80 dark:border-brand-800 text-brand-700 dark:text-brand-300 text-xs font-semibold tracking-wider uppercase mb-6 shadow-xs">
              <span className="w-2 h-2 rounded-full bg-brand-600 dark:bg-brand-400 animate-pulse" />
              MIRA FUTURE TECH VISION PVT. LTD.
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-slate-900 dark:text-white leading-[1.12] tracking-tight mb-6">
              Building Technology <br className="hidden sm:inline" />
              <span className="text-gradient">That Solves Real Problems</span>
            </h1>

            {/* Supporting Text */}
            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-8 font-normal">
              Mira Future Tech Vision develops intelligent technology products and solutions across AI, cybersecurity, healthcare, education and intelligent mobility.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-10">
              <ScrollLink to="products" smooth={true} duration={450} offset={-75}>
                <Button variant="primary" className="w-full sm:w-auto px-7 py-3.5 text-base font-semibold shadow-sm hover:shadow-md">
                  Explore Our Products
                  <ArrowRight size={17} />
                </Button>
              </ScrollLink>
              
              <ScrollLink to="contact" smooth={true} duration={450} offset={-75}>
                <Button variant="outline" className="w-full sm:w-auto px-7 py-3.5 text-base font-medium">
                  Talk to Us
                </Button>
              </ScrollLink>
            </div>

            {/* Credibility highlights */}
            <div className="pt-6 border-t border-slate-200/80 dark:border-slate-800 flex flex-wrap items-center gap-y-3 gap-x-6 text-xs text-slate-500 dark:text-slate-400 font-medium">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-brand-600" /> Enterprise-Grade Security
              </span>
              <span className="flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-indigoBrand-600" /> Modular System Architecture
              </span>
              <span className="flex items-center gap-1.5">
                <Cpu className="w-4 h-4 text-emerald-600" /> Production-Ready AI Platforms
              </span>
            </div>
          </motion.div>

          {/* Right Column: Sophisticated Technology Architecture Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            {/* Visual Container */}
            <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-card overflow-hidden">
              
              {/* Window Header */}
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                  <span className="ml-2 font-mono text-xs text-slate-400 dark:text-slate-500 font-medium">
                    system_topology // mira_mesh
                  </span>
                </div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 text-[11px] font-semibold font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  ACTIVE
                </span>
              </div>

              {/* Connected Nodes Architecture Diagram (Clean Vector) */}
              <div className="relative py-4 px-2">
                <svg className="w-full h-48 md:h-56" viewBox="0 0 400 220" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Subtle Grid Lines */}
                  <line x1="40" y1="40" x2="360" y2="40" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="40" y1="110" x2="360" y2="110" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />
                  <line x1="40" y1="180" x2="360" y2="180" stroke="#f1f5f9" strokeWidth="1" strokeDasharray="3 3" />

                  {/* Connected Data Flow Paths */}
                  <path d="M70 110 L190 60" stroke="#0284c7" strokeWidth="2" strokeDasharray="4 4" className="opacity-60" />
                  <path d="M70 110 L190 160" stroke="#4f46e5" strokeWidth="2" strokeDasharray="4 4" className="opacity-60" />
                  <path d="M190 60 L320 110" stroke="#0284c7" strokeWidth="2" strokeDasharray="4 4" className="opacity-60" />
                  <path d="M190 160 L320 110" stroke="#4f46e5" strokeWidth="2" strokeDasharray="4 4" className="opacity-60" />
                  <path d="M190 60 L190 160" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="2 2" />

                  {/* Node 1: Ingestion Gateway */}
                  <g>
                    <circle cx="70" cy="110" r="28" fill="#f8fafc" stroke="#0284c7" strokeWidth="2" />
                    <circle cx="70" cy="110" r="16" fill="#e0f2fe" />
                    <circle cx="70" cy="110" r="6" fill="#0284c7" />
                    <text x="70" y="152" textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="600" fontFamily="sans-serif">DATA INGESTION</text>
                  </g>

                  {/* Node 2: Neural Inference Engine */}
                  <g>
                    <circle cx="190" cy="60" r="30" fill="#f8fafc" stroke="#4f46e5" strokeWidth="2" />
                    <circle cx="190" cy="60" r="18" fill="#eef2ff" />
                    <circle cx="190" cy="60" r="7" fill="#4f46e5" />
                    <text x="190" y="24" textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="600" fontFamily="sans-serif">AI REASONING</text>
                  </g>

                  {/* Node 3: Zero-Trust Security */}
                  <g>
                    <circle cx="190" cy="160" r="28" fill="#f8fafc" stroke="#059669" strokeWidth="2" />
                    <circle cx="190" cy="160" r="16" fill="#ecfdf5" />
                    <circle cx="190" cy="160" r="6" fill="#059669" />
                    <text x="190" y="202" textAnchor="middle" fill="#64748b" fontSize="10" fontWeight="600" fontFamily="sans-serif">SECURITY LAYER</text>
                  </g>

                  {/* Node 4: Product Delivery Hub */}
                  <g>
                    <circle cx="320" cy="110" r="32" fill="#f8fafc" stroke="#0284c7" strokeWidth="2.5" />
                    <circle cx="320" cy="110" r="20" fill="#e0f2fe" />
                    <circle cx="320" cy="110" r="8" fill="#0284c7" />
                    <text x="320" y="156" textAnchor="middle" fill="#0f172a" fontSize="10" fontWeight="700" fontFamily="sans-serif">MIRA PLATFORM</text>
                  </g>
                </svg>
              </div>

              {/* Telemetry Status Cards inside Visual */}
              <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-3.5 h-3.5 text-brand-600" />
                    <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300">Pipeline Latency</span>
                  </div>
                  <p className="text-base font-bold text-slate-900 dark:text-white font-mono">&lt; 15 ms</p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-3.5 h-3.5 text-indigoBrand-600" />
                    <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-300">Cryptographic Standard</span>
                  </div>
                  <p className="text-base font-bold text-slate-900 dark:text-white font-mono">TLS 1.3 / AES-256</p>
                </div>
              </div>

              {/* Bottom live indicator */}
              <div className="mt-3 text-center">
                <span className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">
                  Modular Microservices • Real-Time Telemetry • Continuous Deployment
                </span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
